import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { useTranslation } from '@/contexts/TranslationContext';
import { FaHeart, FaUtensils, FaMapMarkerAlt } from 'react-icons/fa';
import PaymentModal from './PaymentModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const healthStatusColors = {
  Healthy: 'bg-green-100 text-green-800',
  Recovering: 'bg-yellow-100 text-yellow-800',
  'Needs Care': 'bg-red-100 text-red-800'
};

const DogProfiles = () => {
  const { t } = useTranslation();
  const [dogs, setDogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDog, setSelectedDog] = useState(null);
  const [paymentType, setPaymentType] = useState(null);

  useEffect(() => {
    const fetchDogs = async () => {
      try {
        const response = await axios.get(`${API}/dogs`);
        setDogs(response.data);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching dogs:', error);
        setLoading(false);
      }
    };
    fetchDogs();
  }, []);

  const handleSponsor = (dog) => {
    setSelectedDog(dog);
    setPaymentType('sponsor');
  };

  const handleBowl = (dog) => {
    setSelectedDog(dog);
    setPaymentType('bowl');
  };

  const closeModal = () => {
    setSelectedDog(null);
    setPaymentType(null);
  };

  return (
    <div id="dogs" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            {t('dogs.title')}
          </h2>
          <p className="text-xl text-gray-600">
            {t('dogs.subtitle')}
          </p>
        </motion.div>

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto"></div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {dogs.map((dog, index) => (
              <motion.div
                key={dog.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white border-2 border-gray-200 rounded-2xl overflow-hidden hover:border-emerald-300 hover:shadow-2xl transition-all duration-300"
                data-testid={`dog-card-${dog.id}`}
              >
                <div className="relative h-64 overflow-hidden">
                  <img
                    src={dog.image_url}
                    alt={dog.name}
                    className="w-full h-full object-cover hover:scale-110 transition-transform duration-300"
                  />
                </div>

                <div className="p-6">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">
                    {dog.name}
                  </h3>

                  <p className="text-gray-600 text-sm mb-4">
                    {dog.age} · {dog.breed}
                  </p>

                  {dog.care_venue_name && (
                    <div className="flex items-center gap-2 text-gray-600 text-sm mb-4">
                      <FaMapMarkerAlt className="text-emerald-600" />
                      <span>Cared at <strong>{dog.care_venue_name}</strong> · {dog.district}</span>
                    </div>
                  )}

                  <p className="text-gray-700 leading-relaxed mb-6">
                    {dog.story}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {selectedDog && (
        <PaymentModal
          dog={selectedDog}
          type={paymentType}
          onClose={closeModal}
        />
      )}
    </div>
  );
};

export default DogProfiles;
